import { getJson } from '@/utils/fetchOptions';

import { useMutation } from './useMutation';

export function useBrandsSearchExternal(term: string) {
  const { isLoading, mutate, error, data } = useMutation({
    mutationKey: 'brands-search-external',
    mutationFn: async () => {
      // Validate term before making API call
      if (!term || term.trim() === '') {
        return [
          {
            name: term || '',
            domain: `${term || 'unknown'}.com`,
            logo: undefined
          }
        ];
      }

      // Use Brandfetch Search API to find brands by name
      const searchResponse = await getJson({
        url: `https://api.brandfetch.io/v2/search/${encodeURIComponent(term.trim())}?c=${process.env.NEXT_PUBLIC_BRANDFETCH_CLIENT_ID}`
      });

      if (!searchResponse || 'error' in searchResponse) {
        // If search fails, return a basic entry for manual entry
        return [
          {
            name: term,
            domain: `${term}.com`,
            logo: undefined
          }
        ];
      }

      const results: Api.BrandExternal[] = [];

      // Process each brand found in the search results using Promise.all for better performance
      const brandPromises = searchResponse.map(
        async (brand: { domain: string; name: string; icon?: string }) => {
          try {
            // Get detailed brand information including logo
            const brandDetails = await getJson({
              url: `https://api.brandfetch.io/v2/brands/${brand.domain}`,
              token: process.env.NEXT_PUBLIC_BRANDFETCH_API_KEY
            });

            if (brandDetails && !('error' in brandDetails)) {
              const brandData: Api.BrandExternal = {
                name: brandDetails.name || brand.name || term,
                domain: brand.domain,
                logo: brandDetails.icon || brandDetails.logos?.[0]?.image || undefined
              };
              return brandData;
            }
            // If detailed fetch fails, still include the brand with basic info
            return {
              name: brand.name || term,
              domain: brand.domain,
              logo: brand.icon || undefined
            };
          } catch (brandError) {
            // If detailed fetch fails, still include the brand with basic info
            console.debug(`Failed to fetch details for brand: ${brand.domain}`, brandError);
            return {
              name: brand.name || term,
              domain: brand.domain,
              logo: brand.icon || undefined
            };
          }
        }
      );

      const brandResults = await Promise.all(brandPromises);
      results.push(...brandResults);

      // If no results found, create a basic entry for manual entry
      if (results.length === 0) {
        results.push({
          name: term,
          domain: `${term}.com`,
          logo: undefined
        });
      }

      return results;
    }
  });

  return {
    brandsExternal: data || [],
    isLoading,
    mutate,
    error
  };
}
